import { IPaginator, PatientKind, SubscriptionFrequency } from "common/types";
import { parseNumber } from "common/utils/numbers";
import {
  convertStringPassthrough,
  pluralized,
  splitStringToValidValues,
} from "common/utils/strings";
import { Optional } from "common/utils/types";
import { isValueSet } from "common/utils";
import { toDate } from "common/utils/dates";

import { Paths } from "client/routes";

import { COLOR_SCHEMES, ColorScheme } from "../constants/colorSchemes";
import {
  CarouselDisplayCarousel,
  CategoryCarousel,
  SelectedProductFilters,
  SelectedCategoryProductLineFilters,
  IProductItem,
  SubscriptionSelectItemProps,
  SelectedPromotionProductFilters,
  DiscountedPriceBreak,
  FilterDefinition,
  FilterValue,
  SearchFilterOption,
} from "../types";

// `category === null` is the canonical Top Sellers carousel; remaining
// carousels render as category sections in the order returned by the BE.
export const partitionCarousels = (carousels: CarouselDisplayCarousel[]) => {
  const topSellerCarousel = carousels.find((c) => c.category === null);
  const categoryCarousels = carousels.filter((c): c is CategoryCarousel => c.category !== null);
  return { topSellerCarousel, categoryCarousels };
};

// Top Sellers keeps its own bespoke card and is excluded from the color cycle, which
// starts at the first category carousel (scheme #1).
// `categoryIndex` is the 0-based position within partitionCarousels(...).categoryCarousels.
export const schemeForCategoryIndex = (categoryIndex: number): ColorScheme =>
  COLOR_SCHEMES[categoryIndex % COLOR_SCHEMES.length];

const parsedIds = (ids: Optional<number[] | string>) => {
  if (!ids) {
    return [];
  }
  if (ids instanceof Array) {
    return ids;
  }
  return (
    ids
      .split(",")
      .map((val) => parseNumber(val, 0))
      .filter((val) => !!val) ?? []
  );
};

const parsedBoolean = (bool: Optional<boolean[] | string>) => {
  if (!isValueSet(bool)) {
    return [];
  }
  if (bool instanceof Array) {
    return bool;
  }
  return bool.split(",").map((val) => val === "true");
};

export const getProductFiltersFromSearch = ({
  category_ids,
  manufacturer_ids,
  species,
  incentive_ids,
  rebate_ids,
  prescription_required,
}: Paths["products"]["search"]["Search"]): SelectedProductFilters => {
  const validPatientKinds = new Set(Object.values(PatientKind));
  return {
    category_ids: parsedIds(category_ids),
    manufacturer_ids: parsedIds(manufacturer_ids),
    species: convertStringPassthrough<PatientKind[]>(species ?? "", (kinds) =>
      splitStringToValidValues<PatientKind>(kinds, validPatientKinds),
    ),
    incentive_ids: parsedIds(incentive_ids),
    rebate_ids: parsedIds(rebate_ids),
    prescription_required: parsedBoolean(prescription_required),
  };
};

export const getRebateProductFiltersFromSearch = ({
  category_ids,
  manufacturer_ids,
  species,
  prescription_required,
}: Paths["products"]["rebate"]["Search"]): SelectedPromotionProductFilters => {
  const validPatientKinds = new Set(Object.values(PatientKind));
  return {
    category_ids: parsedIds(category_ids),
    manufacturer_ids: parsedIds(manufacturer_ids),
    species: convertStringPassthrough<PatientKind[]>(species ?? "", (kinds) =>
      splitStringToValidValues<PatientKind>(kinds, validPatientKinds),
    ),
    prescription_required: parsedBoolean(prescription_required),
  };
};

export const getIncentiveProductFiltersFromSearch = ({
  category_ids,
  manufacturer_ids,
  species,
  prescription_required,
}: Paths["products"]["incentive"]["Search"]): SelectedPromotionProductFilters => {
  const validPatientKinds = new Set(Object.values(PatientKind));
  return {
    category_ids: parsedIds(category_ids),
    manufacturer_ids: parsedIds(manufacturer_ids),
    species: convertStringPassthrough<PatientKind[]>(species ?? "", (kinds) =>
      splitStringToValidValues<PatientKind>(kinds, validPatientKinds),
    ),
    prescription_required: parsedBoolean(prescription_required),
  };
};

export const getCategoryProductLineFiltersFromSearch = ({
  manufacturer_ids,
  species,
  prescription_required,
}: Paths["products"]["category"]["Search"]): SelectedCategoryProductLineFilters => {
  const validPatientKinds = new Set(Object.values(PatientKind));
  return {
    manufacturer_ids: parsedIds(manufacturer_ids),
    species: convertStringPassthrough<PatientKind[]>(species ?? "", (kinds) =>
      splitStringToValidValues<PatientKind>(kinds, validPatientKinds),
    ),
    prescription_required: parsedBoolean(prescription_required),
  };
};

/**
 * Every dimension a surface offers, set back to "nothing selected". Leaves `incentive_ids` and
 * `rebate_ids` alone, since no surface offers a control for them and clearing one would widen
 * the results.
 */
export const clearedFilterValues = <TSearch, TValue extends FilterValue, TFilterOptions>(
  definitions: FilterDefinition<TSearch, TValue, TFilterOptions>[],
): Partial<TSearch> =>
  definitions.reduce<Partial<TSearch>>(
    (cleared, definition) => ({ ...cleared, ...definition.toUpdate([]) }),
    {},
  );

/**
 * Indents child categories under their parent, one level only. A child whose parent is missing
 * from the options is promoted to the top level rather than dropped.
 */
export const nestByParentCategory = (
  options: SearchFilterOption<FilterValue>[],
): { option: SearchFilterOption<FilterValue>; depth: number }[] => {
  const optionsByValue = new Map(options.map((option) => [option.value, option]));
  const isPrimaryCategory = (option: SearchFilterOption<FilterValue>) =>
    option.parent_id == null || !optionsByValue.has(option.parent_id);

  const primaryOptions = options.filter(isPrimaryCategory);
  const childOptions = options.filter((option) => !isPrimaryCategory(option));

  return primaryOptions.flatMap((primaryOption) => [
    { option: primaryOption, depth: 0 },
    ...childOptions
      .filter((childOption) => childOption.parent_id === primaryOption.value)
      .map((childOption) => ({ option: childOption, depth: 1 })),
  ]);
};

/**
 * Top-level options fill the limit first, then children backfill what is left. Ranking the two
 * together would let the largest subtree bury every other top-level option, since a parent's
 * count is the sum of its children's. A selection past the cut is added back with its parent.
 */
export const collapsedOptions = <TValue extends FilterValue>({
  options,
  selected,
  limit,
}: {
  options: SearchFilterOption<TValue>[];
  selected: TValue[];
  limit: number;
}): SearchFilterOption<TValue>[] => {
  const selectedValues = new Set<FilterValue>(selected);
  const optionsByValue = new Map<FilterValue, SearchFilterOption<TValue>>(
    options.map((option) => [option.value, option]),
  );
  const isTopLevel = (option: SearchFilterOption<TValue>) =>
    option.parent_id == null || !optionsByValue.has(option.parent_id);

  const optionsToShow = new Map<FilterValue, SearchFilterOption<TValue>>();
  const keepUntilLimitReached = (candidates: SearchFilterOption<TValue>[]) =>
    candidates.forEach((option) => {
      if (optionsToShow.size < limit) {
        optionsToShow.set(option.value, option);
      }
    });

  keepUntilLimitReached(options.filter(isTopLevel));
  keepUntilLimitReached(options);
  options
    .filter((option) => selectedValues.has(option.value))
    .forEach((option) => {
      optionsToShow.set(option.value, option);
      const parentOption =
        option.parent_id != null ? optionsByValue.get(option.parent_id) : undefined;
      if (parentOption) {
        optionsToShow.set(parentOption.value, parentOption);
      }
    });

  return options.filter((option) => optionsToShow.has(option.value));
};

/**
 * Decided from the whole option set the backend offers rather than from what the current
 * narrowing returned, so a filter cannot vanish as the shopper narrows. Hidden only at zero
 * options, which is what a surface pinning that dimension produces; one option just means the
 * catalog holds a single value.
 */
export const filtersWithOptions = <TSearch, TFilterOptions>({
  definitions,
  filterOptions,
}: {
  definitions: FilterDefinition<TSearch, FilterValue, TFilterOptions>[];
  filterOptions: TFilterOptions;
}) =>
  definitions.flatMap((definition) => {
    const options = definition.optionsFrom(filterOptions);
    return options.length ? [{ definition, options }] : [];
  });

export const hasAnySelectedFilterValue = <TSearch, TFilterOptions>(
  definitions: FilterDefinition<TSearch, FilterValue, TFilterOptions>[],
  filters: TSearch,
) => definitions.some((definition) => definition.selectedFrom(filters).length > 0);

export const getSearchString = (paginator?: IPaginator, entityDescription: string = "result") => {
  const start = paginator?.page_start_index || 0;
  const end = paginator?.page_end_index || 0;
  const count = paginator?.count || 0;
  const entity = pluralized(count, entityDescription);
  return `Showing ${start} - ${end} of ${count} ${entity}`;
};

/* Discounted per-unit price at a given quantity, respecting vendor quantity tiers.

   Items with multiple price breaks are priced per tier; the discounted_price_breaks
   carry the rebate/subscription discount already applied per tier, so the selected
   quantity resolves to the correct discounted price. Items with a single (or no)
   tier fall back to the headline discounted fields. */
export const getItemPriceFromQuantity = (
  item: IProductItem | SubscriptionSelectItemProps,
  quantity: number,
  subscription: SubscriptionFrequency | null,
  discountedPriceBreaks: DiscountedPriceBreak[],
) => {
  if (discountedPriceBreaks.length <= 1) {
    return subscription
      ? item.subscription_discounted_price
      : item.non_subscription_discounted_price;
  }
  const relevantPriceBreak = discountedPriceBreaks.find(
    (priceBreak) => quantity >= priceBreak.low_qty && quantity <= priceBreak.high_qty,
  );
  if (relevantPriceBreak) {
    return subscription
      ? relevantPriceBreak.subscription_discounted_price
      : relevantPriceBreak.non_subscription_discounted_price;
  }
  return subscription ? item.subscription_discounted_price : item.non_subscription_discounted_price;
};

export const toPromotionDateRange = (
  start: string | null = null,
  end: string | null = null,
): string => {
  if (start && end) {
    return `Offer valid from ${toDate(start)} to ${toDate(end)}`;
  }
  if (!start && end) {
    return `Offer valid until ${toDate(end)}`;
  }
  if (start && !end) {
    return `Offer valid since ${toDate(start)}`;
  }
  return "Offer always valid. No expiration";
};

type PromoCodePunctuation = {
  [key: string]: string;
};

/**
 * Determines the proper punctuation and "or"s to be added between promo codes.
 *
 * @param codes The promo codes that the user can choose from
 * @returns A dictionary where key is promo code and value is string to prepend
 */
export const getPromoCodePunctuation = (codes: string[]) => {
  const numCodes = codes.length;
  const stringsToPrepend: PromoCodePunctuation = {};

  codes.forEach((code, index) => {
    if (index === 0) {
      stringsToPrepend[code] = "";
    } else if (index === numCodes - 1) {
      stringsToPrepend[code] = numCodes === 2 ? " or " : ", or ";
    } else {
      stringsToPrepend[code] = ", ";
    }
  });
  return stringsToPrepend;
};

/**
 * Calculate dose price from unit price and units count.
 * Returns null if units_count is not available or zero, or if unitPrice is not a valid number.
 */
export const calculateDosePrice = (unitPrice: number, unitsCount: number | null): number | null => {
  if (!unitsCount || unitsCount <= 0 || !Number.isFinite(unitPrice)) {
    return null;
  }
  return Math.round(unitPrice / unitsCount);
};

/**
 * Check if dose pricing is available for an item.
 * Validates units_count and dose_label to ensure complete dose pricing display.
 */
export const hasDosePricing = (item: {
  units_count: number | null;
  dose_label: string | null;
}): boolean => {
  return item.units_count !== null && item.units_count > 0 && item.dose_label !== null;
};
